import { useCallback, useEffect, useRef, useState } from "react";
import { apiClient } from "../lib/api";

/**
 * useNovaLiveEvents — polls the unified real-event stream
 * (GET /api/floor/nova/live-events, see core/nova_live.py) that merges the
 * Kaizen intake cascade and the trip-risk reassignment autonomy behind one
 * vocabulary. Keeps a rolling buffer so "Follow Nova" can both react to
 * the newest event live and let a VA reopen any earlier one from the list.
 *
 * Never fabricates anything — an empty response just means Nova hasn't
 * done anything autonomous recently.
 */
const BUFFER_SIZE = 60;

export function useNovaLiveEvents({ pollMs = 3000 } = {}) {
  const [events, setEvents] = useState([]);
  const [latestEvent, setLatestEvent] = useState(null);
  const sinceRef = useRef(null);
  const seenIdsRef = useRef(new Set());

  const poll = useCallback(async () => {
    try {
      const { data } = await apiClient.get("/floor/nova/live-events", {
        params: { since: sinceRef.current || undefined, limit: 40 },
      });
      const fresh = (data?.events || []).filter(e => !seenIdsRef.current.has(e.id));
      if (fresh.length) {
        fresh.forEach(e => seenIdsRef.current.add(e.id));
        setEvents(prev => {
          const merged = [...fresh, ...prev].slice(0, BUFFER_SIZE);
          // keep the seen-id set bounded to what's actually still in the buffer
          seenIdsRef.current = new Set(merged.map(e => e.id));
          return merged;
        });
        // newest-first — fresh[0] is the most recent new event this tick
        setLatestEvent(fresh[0]);
        sinceRef.current = data.server_time || fresh[0].ts;
      } else if (data?.server_time) {
        sinceRef.current = data.server_time;
      }
    } catch { /* best-effort — never breaks the Floor if this fails */ }
  }, []);

  useEffect(() => {
    poll();
    const id = setInterval(poll, pollMs);
    return () => clearInterval(id);
  }, [poll, pollMs]);

  return { events, latestEvent };
}
